import * as React from "react";

import { getRTL } from "@fluentui/react/lib/Utilities";

import { AppBaseButton } from "@1js/acui-button";
import { dataUniqueId } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import {
  checkExperiment,
  isShyRibbonEnabled,
  ribbonExperiments,
} from "@1js/acui-experiments";
import { iconNameToIconProps } from "@1js/acui-icons";
import { AppTooltipHost } from "@1js/acui-tooltip";
import { isNextStyleEnabled } from "@1js/acui-theme";

import type { AppPaddleButtonProps } from "./AppPaddleButton.Props";
import { getPaddleButtonStyles } from "./AppPaddleButton.styles";
import { AppPaddleButton as AppPaddleButtonNext } from "./next/AppPaddleButton";
import { isV9RibbonButtonEnabled } from "@1js/acui-experiments";
import type { AppPaddleButtonProps as AppPaddleButtonPropsNext } from "./next";

export class AppPaddleButtonCurrent extends React.PureComponent<AppPaddleButtonProps> {
  private tooltipHost: AppTooltipHost | undefined;
  private baseButton = React.createRef<AppBaseButton>();
  private baseButtonWrapper = React.createRef<HTMLDivElement>();

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public render() {
    // There are still some cases where we need to remove the extra padding for the toggle
    const noRibbonToggle =
      !isShyRibbonEnabled(this.context) &&
      !checkExperiment(
        this.context,
        ribbonExperiments.multilineRibbonIsEnabled
      );
    return (
      <AppBaseButton
        ref={this.baseButton}
        elementRef={this.baseButtonWrapper}
        label={this.props.label}
        styles={getPaddleButtonStyles(
          this.props.orientation,
          getRTL(),
          this.props.disableModeTogglePadding || noRibbonToggle
        )}
        data-unique-id={dataUniqueId(this.props)}
        iconProps={iconNameToIconProps(this.props.icon)}
        onClick={this.onExecute}
        tabIndex={-1}
        data-is-focusable={false}
        ariaLabel={this.props.label}
        takeFocusOnClick={false}
      />
    );
  }

  public componentDidMount() {
    this.initializeTooltipHost();
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
      this.tooltipHost = undefined;
    }
  }

  private initializeTooltipHost() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        htmlElement: this.baseButtonWrapper.current,
        controlId: this.props.id,
        controlLabel: this.props.label,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: true,
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  private onExecute = () => {
    this.props.onExecute(this.props.id);
  };
}

export const AppPaddleButton = React.forwardRef<
  HTMLButtonElement,
  AppPaddleButtonProps
>((props, ref) => {
  const context = React.useContext(AppContext);

  const v9AppPaddleButtonEnabled = isV9RibbonButtonEnabled(context);
  const isV9RibbonControlsEnabled =
    v9AppPaddleButtonEnabled ||
    (!!checkExperiment(
      context,
      ribbonExperiments.fluentV9RibbonControlsIsEnabled
    ) &&
      isNextStyleEnabled());

  return isV9RibbonControlsEnabled ? (
    <AppPaddleButtonNext ref={ref} {...(props as AppPaddleButtonPropsNext)} />
  ) : (
    <AppPaddleButtonCurrent ref={ref as any} {...props} />
  );
});

AppPaddleButton.displayName = "AppPaddleButton";
